import { useMutation, useQueryClient } from "@tanstack/react-query";
import { skipioApi, touchSkipioApi } from "../../api";
import usePlan from "../../hooks/use-plan";
import { useToast } from "../../hooks/use-toast";
import {
  CampaignResponse,
  createCampaign,
  fetchCampaign,
  MessageTemplateType,
  updateCampaign,
} from "../../api/campaigns";
import { EditableCampaign } from ".";

function transformCampaignForApi(campaign: EditableCampaign) {
  return {
    ...campaign,
    messageTemplates: campaign.messageTemplates.map((template) => {
      // TODO: Use shared type
      const messageTemplateType: MessageTemplateType = template.voicemailId
        ? "VoicemailMessageTemplate"
        : "SmsMessageTemplate";
      return {
        ...template,
        delayInDays: Math.max(1, template.delayInDays),
        delayInMinutes: Math.max(1, template.delayInMinutes),
        daysBeforeEvent: Math.max(1, Number(template.daysBeforeEvent)),
        daysAfterEvent: Math.max(1, Number(template.daysAfterEvent)),
        minutesBeforeEvent: Math.max(1, Number(template.minutesBeforeEvent)),
        minutesAfterEvent: Math.max(1, Number(template.minutesAfterEvent)),
        mediaUrls: template.imageUrl ? [template.imageUrl] : [],
        mediaMimeTypes: template.imageType ? [template.imageType] : [],
        deliverAtTime: template.deliverAt
          ? template.deliverAt.toISOString()
          : null,
        messageTemplateType: messageTemplateType,
      };
    }),
  };
}

export function useSaveCampaign() {
  const queryClient = useQueryClient();
  const createCampaignMutation = useMutation({
    mutationFn: createCampaign,
  });
  const updateCampaignMutation = useMutation({
    mutationFn: updateCampaign,
  });
  const plan = usePlan();
  const showToast = useToast();

  return async function saveCampaign(campaign: EditableCampaign) {
    const apiTransformedCampaign = transformCampaignForApi(campaign);

    let newCampaign: CampaignResponse;
    try {
      if (campaign.id) {
        newCampaign = await updateCampaignMutation.mutateAsync({
          id: campaign.id,
          campaign: apiTransformedCampaign,
        });
      } else {
        newCampaign = await createCampaignMutation.mutateAsync({
          campaign: apiTransformedCampaign,
        });
      }
    } catch (e) {
      if (!e.response.data || Array.isArray(e.response.data.errors)) throw e;

      throw new CampaignValidationError(
        e.response.data.errors,
        e.response.data.data
      );
    }

    const deletedIds = (newCampaign.contactIds || []).filter(
      (id) => !campaign.contactIds.includes(id)
    );

    const addedIds = (campaign.contactIds || []).filter(
      (id) => !newCampaign.contactIds.includes(id)
    );

    if (addedIds.length + deletedIds.length === 0) {
      return newCampaign;
    }

    if (deletedIds.length) {
      await skipioApi.campaigns[newCampaign.id].contacts.delete(
        {},
        { data: { contactId: [...deletedIds], skipDefer: true } }
      );
    }

    if (addedIds.length) {
      if (plan.features.paused) {
        showToast(
          "Adding contacts to campaigns is disabled because your account is paused.",
          {
            error: true,
          }
        );
      } else {
        await skipioApi.campaigns[newCampaign.id].contacts.post(
          {},
          { data: { contactId: [...addedIds], skipDefer: true } }
        );
      }
    }

    await touchSkipioApi("campaigns");
    const c = await queryClient.fetchQuery({
      queryKey: ["campaigns", newCampaign.id],
      queryFn: () => fetchCampaign(newCampaign.id),
    });
    return c;
  };
}

export class CampaignValidationError extends Error {
  errors: Record<string, string[]>;
  campaign: CampaignResponse;

  constructor(errors: Record<string, string[]>, campaign: CampaignResponse) {
    super();
    this.errors = errors;
    this.campaign = campaign;
  }
}

export function fixPositions<T>(item: T & { position: number }, index: number) {
  if (item.position === index) return item;
  else {
    return {
      ...item,
      position: index,
    };
  }
}
